import type { HyphenationRules } from './hyphenate';
import { HYPHEN_EN_US_TRIE_BASE64 } from './hyphen-en-trie';

// Port of the `hypher` 0.1.8 trie walk (MIT OR Apache-2.0) over the trie the Rust layout embeds: canvas and print break alike.

const ASCII_WORD = /^[A-Za-z]+$/;

let trie: Uint8Array | undefined;

function trieBytes(): Uint8Array {
  trie ??= Uint8Array.from(atob(HYPHEN_EN_US_TRIE_BASE64), c => c.charCodeAt(0));
  return trie;
}

interface State {
  addr: number;
  stride: number;
  levels: number;
  levelCount: number;
  trans: number;
  count: number;
}

function stateAt(d: Uint8Array, addr: number): State {
  let pos = addr;
  const head = d[pos++];
  const stride = (head >> 5) & 3;
  let count = head & 31;
  if (count === 31) count = d[pos++];
  let levels = 0;
  let levelCount = 0;
  if (head >> 7 !== 0) {
    levels = (d[pos] << 4) | (d[pos + 1] >> 4);
    levelCount = d[pos + 1] & 15;
    pos += 2;
  }

  return { addr, stride, levels, levelCount, trans: pos, count };
}

function transition(d: Uint8Array, s: State, byte: number): State | undefined {
  for (let i = 0; i < s.count; i++) {
    if (d[s.trans + i] !== byte) continue;
    const o = s.trans + s.count + s.stride * i;
    const delta =
      s.stride === 1
        ? (d[o] << 24) >> 24
        : s.stride === 2
          ? (((d[o] << 8) | d[o + 1]) << 16) >> 16
          : ((d[o] << 16) | (d[o + 1] << 8) | d[o + 2]) - (1 << 23);
    return stateAt(d, s.addr + delta);
  }

  return undefined;
}

/** Points of an ASCII word (index of the letter the next line starts with) by the English patterns; else none. */
export function englishHyphenationPoints(word: string, rules: HyphenationRules): number[] {
  if (word.length < Math.max(rules.minWordLength, 2) || !ASCII_WORD.test(word)) return [];
  const d = trieBytes();
  const root = stateAt(d, ((d[0] << 24) | (d[1] << 16) | (d[2] << 8) | d[3]) >>> 0);
  const dotted = `.${word.toLowerCase()}.`;
  const minIdx = 1 + Math.max(rules.minPrefix, 1);
  const maxIdx = 1 + word.length - Math.max(rules.minSuffix, 1);
  const levels = new Array<number>(word.length - 1).fill(0);

  for (let start = 0; start < dotted.length; start++) {
    let state = root;
    for (let k = start; k < dotted.length; k++) {
      const next = transition(d, state, dotted.charCodeAt(k));
      if (!next) break;
      state = next;
      let offset = 0;
      for (let i = 0; i < state.levelCount; i++) {
        const packed = d[state.levels + i];
        offset += Math.floor(packed / 10);
        const split = start + offset;
        if (split >= minIdx && split <= maxIdx) levels[split - 2] = Math.max(levels[split - 2], packed % 10);
      }
    }
  }

  const points: number[] = [];
  levels.forEach((level, i) => {
    if (level % 2 === 1) points.push(i + 1);
  });
  return points;
}
